import React, { createContext, useState, useContext, useEffect } from 'react';
import { customerLogin, customerSignup, adminLogin, signOut } from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored session
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (userData, isAdmin = false) => {
    if (!userData.email || !userData.password) {
      throw new Error('Email and password are required');
    }

    try {
      const user = isAdmin 
        ? await adminLogin(userData.email, userData.password)
        : await customerLogin(userData.email, userData.password);
      
      setCurrentUser(user);
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      throw error;
    }
  };

  const signup = async (userData) => {
    if (!userData.email || !userData.password) {
      throw new Error('Email and password are required');
    }

    try {
      const user = await customerSignup(userData);
      setCurrentUser(user);
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setCurrentUser(null);
    } catch (error) {
      throw error;
    }
  };

  const value = {
    currentUser,
    login,
    signup,
    logout,
    loading,
    isAdmin: currentUser?.role === 'admin',
    isCustomer: currentUser?.role === 'customer'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
